// Textos del "?" de los widgets de los dashboards de After Sales de Honda (HON-71).
// Honda pidió que cada "?" diga qué pregunta responde la gráfica y cómo se compone el dato.

const PERIODO = 'Considera las encuestas del periodo y los filtros seleccionados.'

const help = (pregunta: string, calculo: string) =>
  `<b>Pregunta:</b> ${pregunta}<br/><b>Cómo se calcula:</b> ${calculo}`

const describe = (queMuestra: string, calculo: string) =>
  `<b>Qué muestra:</b> ${queMuestra}<br/><b>Cómo se calcula:</b> ${calculo}`

const CALCULO = {
  // Indicadores hon_*_npm_* (fórmula wavg)
  promedio: `Promedio de las calificaciones de los clientes, expresado en escala de 0 a 100%. ${PERIODO}`,
  // Indicadores count-number (gráficas de pastel)
  distribucion: `Porcentaje de clientes que eligió cada respuesta, sobre el total de respuestas a la pregunta. ${PERIODO}`,
  comentarios: `Últimos comentarios abiertos que dejaron los clientes en esta pregunta. ${PERIODO}`,
  nps: `NPS = % de promotores (calificación 9 o 10) menos % de detractores (calificación de 6 o menos). ${PERIODO}`,
}

const SEMAFORO = 'Color: rojo hasta 69%, amarillo de 70% a 89% y verde desde 90%.'

export const hondaHelp = {
  satisfaccionGeneral: (preguntaPrincipal: string) => describe(
    'La satisfacción general del cliente y, en la vista de radar, el desempeño en las 5 etapas del proceso de servicio.',
    `Línea: NPS de la pregunta "${preguntaPrincipal}" por mes. Radar: % que obtiene cada etapa (puntos obtenidos / puntos máximos de la etapa). ${PERIODO}`
  ),
  npsTendencia: (pregunta: string) => help(pregunta,
    `${CALCULO.nps} Se muestra el periodo actual y los meses anteriores con su % de promotores, neutros y detractores.`
  ),
  npsDetalle: describe(
    'La distribución de promotores, neutros y detractores del NPS por mes.',
    `% de clientes en cada grupo; la primera barra es el total del periodo y las siguientes, cada mes. ${PERIODO}`
  ),
  encuestasPorMes: describe(
    'Cuántas encuestas respondieron los clientes cada mes.',
    `Conteo de encuestas contestadas por mes; la flecha compara contra el mes anterior. ${PERIODO}`
  ),
  index: describe(
    'El índice compuesto de postventa: qué tanto cumple el distribuidor con el proceso de servicio.',
    'Promedio de los puntos que obtuvo cada encuesta contra el promedio de puntos máximos posibles ("de ..."). '
    + 'El máximo de cada encuesta depende de las preguntas que le aplicaron al cliente según su tipo de visita. '
    + `${SEMAFORO} El color se evalúa sobre el porcentaje (puntos / máximo). ${PERIODO}`
  ),
  calidadDatos: describe(
    'El porcentaje de encuestas con datos válidos.',
    `Encuestas que no fueron marcadas como inválidas / total de encuestas. ${PERIODO}`
  ),
  respuestas: describe(
    'El porcentaje de encuestas que los clientes terminaron de contestar.',
    `Encuestas contestadas / (encuestas contestadas + encuestas en progreso). ${PERIODO}`
  ),
  procesoPostventa: describe(
    'El desempeño en cada etapa del proceso de servicio: Iniciación, Asesor, Instalaciones, Entrega y Calidad.',
    `Por etapa: puntos promedio obtenidos / puntos máximos promedio de la etapa × 100. Los puntos de las 5 etapas suman el Index. ${SEMAFORO} ${PERIODO}`
  ),
  ranking: (queEvalua: string, que: 'mejores' | 'peores', cuantos: string) => describe(
    `Los ${cuantos} con ${que} resultados en ${queEvalua}.`,
    `Se calcula ${queEvalua} de cada uno y se ordenan de ${que === 'mejores' ? 'mayor a menor' : 'menor a mayor'}. ${PERIODO}`
  ),
  nubePalabras: describe(
    'Las palabras que más se repiten en los comentarios abiertos de los clientes.',
    `Entre más grande la palabra, más veces aparece en los comentarios. ${PERIODO}`
  ),
  atributoPositivo: describe(
    'Los temas que los clientes mencionan con más frecuencia en comentarios positivos.',
    `Los comentarios se clasifican automáticamente por tema y sentimiento; se cuentan las menciones positivas de cada tema. ${PERIODO}`
  ),
  atributoNegativo: describe(
    'Los temas que los clientes mencionan con más frecuencia en comentarios negativos.',
    `Los comentarios se clasifican automáticamente por tema y sentimiento; se cuentan las menciones negativas de cada tema. ${PERIODO}`
  ),
  analisisSentimientos: describe(
    'Los temas detectados en los comentarios y el sentimiento de los clientes que los mencionan.',
    `Por tema: total de menciones y % de comentarios de promotores, neutros y detractores. ${PERIODO}`
  ),
  ishikawa: describe(
    'Las principales causas de insatisfacción, agrupadas por categoría.',
    `Temas con más menciones negativas en los comentarios de los clientes. ${PERIODO}`
  ),
  npsPor: (nivel: string) => describe(`El NPS de cada ${nivel}.`, CALCULO.nps),
}

/**
 * Agrega el "?" a los widgets de una sola pregunta de la encuesta (indicadores hon_postvta_* /
 * hon_moto_pv_*) que no tengan helpText propio. La pregunta sale del título del widget o de
 * `preguntas` (para títulos abreviados); el cálculo depende del tipo de indicador.
 */
export const withHondaQuestionHelp = (widgets: any[], preguntas: Record<string, string> = {}) =>
  widgets.map((widget: any) => {
    const indicators = widget?.config?.indicators
    const indicator = indicators?.length === 1 ? indicators[0]?.indicator : undefined
    if (widget?.helpText || !/^hon_(postvta|moto_pv)_/.test(indicator ?? '')) {
      return widget
    }
    const calculo = widget.config?.type === 'comments'
      ? CALCULO.comentarios
      : /_npm_/.test(indicator) ? CALCULO.promedio : CALCULO.distribucion
    return {
      ...widget,
      showHelp: true,
      helpText: help(preguntas[indicator] ?? widget.title, calculo),
    }
  })
